import { useState } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import { cn } from "@utils/tailwind.util";
import { SubscribeScreenAction } from "@enums/screens.enum";
import { Paragraph } from "@ui/Content";
import { Spinner } from "@ui/Loaders";
import { Button } from "@ui/Button";
import { H3 } from "@ui/Headings";
import { View } from "@ui/View";

interface Props {
  isPending: boolean;
  onSubmit: (subscribeFromCurrent: boolean) => void;
  syncedEmailsCount: number;
}

export function SubscribeSection({ isPending, onSubmit, syncedEmailsCount }: Props) {
  const { track } = useUserpilot();
  const [action, setAction] = useState<SubscribeScreenAction | null>(null);

  const handleSubscribeFromCurrentEmail = () => {
    setAction(SubscribeScreenAction.SubscribeFromCurrent);
    onSubmit(true);
    track("Click: Save email");
  };

  const handleSubscribeFullThread = () => {
    setAction(SubscribeScreenAction.SubscribeFull);
    onSubmit(false);
    track("Click: Subscribe full thread");
  };

  return (
    <>
      <View.Condition if={syncedEmailsCount < 2}>
        <section className="flex flex-col gap-3 pt-8 px-2 border-t dark:border-gray-700 animate-fade-in">
          <Paragraph>
            Sync future emails automatically.
            <br />
            Subscribe to keep everything
            <br />
            flowing into Meridian.
          </Paragraph>

          <Button
            buttonType={"PRIMARY"}
            onClick={handleSubscribeFromCurrentEmail}
            disabled={isPending}
          >
            {isPending ? <Spinner size={20} /> : "Subscribe"}
          </Button>
        </section>
      </View.Condition>

      <View.Condition if={syncedEmailsCount > 1}>
        <section className="flex flex-col gap-3 pt-8 px-2 border-t dark:border-gray-700 animate-fade-in">
          <H3>Some emails in this thread have already been synced to Meridian</H3>
          <Paragraph>
            Subscribe to the full thread from the first synced message, or only from this email
            onward.
          </Paragraph>

          <View.Condition if={action !== SubscribeScreenAction.SubscribeFromCurrent}>
            <Button
              className={cn(
                "min-w-[270px] h-8",
                isPending
                  ? "bg-teal-100 text-white dark:bg-gray-700 dark:text-teal-100"
                  : "bg-teal-500 text-white"
              )}
              buttonType={isPending ? "TRANSPARENT" : "PRIMARY"}
              onClick={handleSubscribeFromCurrentEmail}
              disabled={isPending}
            >
              {isPending ? <Spinner size={20} /> : "Subscribe Thread"}
            </Button>
          </View.Condition>

          <View.Condition if={!action}>
            <div className="flex justify-center w-full">
              <span className="text-gray-950 dark:text-white text-sm font-[400] leading-none">
                or
              </span>
            </div>
          </View.Condition>

          <View.Condition if={action !== SubscribeScreenAction.SubscribeFull}>
            <Button
              className={cn(
                "min-w-[270px] h-8",
                isPending
                  ? "bg-gray-50 dark:bg-gray-700 dark:text-teal-100"
                  : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80"
              )}
              buttonType="TRANSPARENT"
              onClick={handleSubscribeFullThread}
              disabled={isPending}
            >
              {isPending ? <Spinner size={20} /> : "Subscribe Only to Following Emails"}
            </Button>
          </View.Condition>
        </section>
      </View.Condition>
    </>
  );
}
